/**
 * AppErrorBoundary — top-level safety net so a render-time exception inside
 * any page never leaves the user staring at a blank white screen. Logs the
 * error to console + shows a friendly card with reload / go-home buttons.
 */
import React from "react";
import { AlertTriangle, RotateCcw, Home } from "lucide-react";

export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("AppErrorBoundary caught:", error, info?.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    const msg = this.state.error?.message || "Unknown error";
    return (
      <div className="min-h-screen flex items-center justify-center px-5 py-10" style={{background: "var(--bg-page, #070C18)"}} data-testid="app-error-boundary">
        <div className="max-w-md w-full rounded-2xl border border-rose-500/30 bg-slate-900/90 p-7 text-slate-100 shadow-xl">
          <AlertTriangle size={28} className="text-rose-300 mb-3"/>
          <h1 className="text-xl font-bold" style={{fontFamily:"Outfit"}}>Something went wrong</h1>
          <p className="text-sm text-slate-400 mt-2">
            The page hit an unexpected error. Your data isn't lost — try reloading the page.
            If the problem persists, please contact <a href="mailto:sales@optivize.digital" className="text-cyan-400">sales@optivize.digital</a>.
          </p>
          <pre className="mt-3 text-[11px] text-slate-500 bg-slate-950/60 border border-slate-800 rounded-md p-2 overflow-x-auto max-h-32" data-testid="error-message">
            {msg}
          </pre>
          <div className="mt-5 flex gap-2">
            <button
              onClick={() => window.location.reload()}
              className="btn-primary inline-flex items-center gap-1.5 text-sm"
              data-testid="error-reload-btn"
            >
              <RotateCcw size={14}/> Reload page
            </button>
            <a href="/" className="btn-ghost inline-flex items-center gap-1.5 text-sm" data-testid="error-home-btn">
              <Home size={14}/> Go home
            </a>
          </div>
        </div>
      </div>
    );
  }
}
